import { AlertTriangle, LogOut } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { toast } from 'sonner'

export function ImpersonationBanner() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { user, stopImpersonation } = useAuth()

    if (!user?.is_impersonating) {
        return null
    }

    const displayName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email

    return (
        <div className="flex flex-col gap-3 border-b border-amber-300/60 bg-amber-50 px-4 py-3 text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 min-w-0">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0">
                    <p className="text-sm font-semibold">
                        {t('impersonation.banner_title', 'Impersonation active')}
                    </p>
                    <p className="text-xs opacity-90 truncate">
                        {t('impersonation.banner_body', 'You are viewing the app as {{name}} ({{email}}).', {
                            name: displayName,
                            email: user.email,
                        })}
                        {user.impersonator_email
                            ? ` ${t('impersonation.banner_actor', 'Signed in as super admin {{email}}.', {
                                  email: user.impersonator_email,
                              })}`
                            : ''}
                    </p>
                </div>
            </div>
            <Button
                type="button"
                size="sm"
                variant="outline"
                className="shrink-0 border-amber-400/70 bg-white/70 text-amber-950 hover:bg-amber-100 hover:text-amber-950 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-100 dark:hover:bg-amber-900/60 dark:hover:text-amber-100"
                onClick={() => {
                    void stopImpersonation()
                        .then(() => navigate('/admin/users'))
                        .catch((err: any) => {
                            toast.error(
                                err.response?.data?.detail
                                || t('impersonation.stop_failed', 'Could not exit impersonation.'),
                            )
                        })
                }}
            >
                <LogOut className="mr-2 h-4 w-4" />
                {t('impersonation.exit', 'Exit impersonation')}
            </Button>
        </div>
    )
}
